import React, { useEffect, useMemo, useRef, useState } from "react";
import { Play, Pause, SkipForward, SkipBack, ArrowLeft, CheckCircle2, Dumbbell, Flame, Plus, Pencil, Trash2, CalendarDays, Volume2, VolumeX, Star } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api";
import { useLang } from "@/i18n";
import { bell, beep, fmt } from "@/lib/ringaudio";
import { blockLine, coachPhrases, useCoachVoice } from "@/lib/coachVoice";
import { WORKOUTS, KIND_META, customToWorkout } from "@/data/workouts";
import ProgramBuilder from "@/pages/ProgramBuilder";
import WeeklyCalendar from "@/pages/WeeklyCalendar";

const authHeader = () => ({ headers: { "X-Access-Token": localStorage.getItem("guardia_bp_token") || "" } });

const LEVEL_LABEL = {
  it: ["Personalizzato", "Intermedio 1", "Intermedio 2", "Intermedio 3", "Intermedio 4", "Intermedio 5"],
  en: ["Custom", "Intermediate 1", "Intermediate 2", "Intermediate 3", "Intermediate 4", "Intermediate 5"],
};

export default function WorkoutsTab({ initialProgram }) {
  const { lang } = useLang();
  const [view, setView] = useState("list");
  const [active, setActive] = useState(null);
  const [editing, setEditing] = useState(null);
  const [customs, setCustoms] = useState(null);
  const pending = useRef(initialProgram);

  const load = () => api.get("/blueprint/programs", authHeader()).then((r) => setCustoms(r.data)).catch(() => setCustoms([]));
  useEffect(() => { load(); }, []);

  const customWorkouts = useMemo(() => (customs || []).map(customToWorkout), [customs]);
  const all = useMemo(() => [...WORKOUTS, ...customWorkouts], [customWorkouts]);

  useEffect(() => {
    if (!pending.current || customs === null) return;
    const w = all.find((x) => x.id === pending.current);
    pending.current = null;
    if (w) setActive(w);
  }, [customs, all]);

  const remove = async (w) => {
    if (!window.confirm(lang === "it" ? `Eliminare "${w.title.it}"?` : `Delete "${w.title.en}"?`)) return;
    await api.delete(`/blueprint/programs/${w.raw.id}`, authHeader());
    toast.success(lang === "it" ? "Programma eliminato" : "Program deleted");
    load();
  };

  if (active) return <WorkoutPlayer workout={active} onExit={() => setActive(null)} />;
  if (editing) return <ProgramBuilder initial={editing.raw} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />;

  const subTabs = [
    { id: "list", label: lang === "it" ? "Programmi" : "Programs", icon: Dumbbell },
    { id: "calendar", label: lang === "it" ? "Calendario" : "Calendar", icon: CalendarDays },
  ];

  return (
    <div>
      <div className="flex gap-2 mb-6">
        {subTabs.map((s) => (
          <button key={s.id} onClick={() => setView(s.id)} data-testid={`workouts-view-${s.id}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border transition-colors ${view === s.id ? "bg-white text-black border-white" : "border-[#272b35] text-zinc-400 hover:text-white"}`}>
            <s.icon size={13} /> {s.label}
          </button>
        ))}
      </div>
      {view === "calendar" ? (
        <WeeklyCalendar programs={all} onStart={setActive} />
      ) : (
        <ProgramList lang={lang} customs={customWorkouts} onStart={setActive} onEdit={setEditing} onDelete={remove} onCreate={() => setEditing({ raw: null })} />
      )}
    </div>
  );
}

function ProgramCard({ w, lang, onStart, onEdit, onDelete }) {
  return (
    <div data-testid={`workout-card-${w.id}`} className="relative bg-[#14161b] border border-[#272b35] hover:border-[#3a3f4b] rounded-xl p-5 transition-colors group">
      <button onClick={() => onStart(w)} className="absolute inset-0 rounded-xl" aria-label="start" data-testid={`workout-start-${w.id}`} />
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded text-white" style={{ backgroundColor: w.color }}>
          {LEVEL_LABEL[lang][w.level]}
        </span>
        {w.custom ? (
          <span className="relative z-10 flex gap-1">
            <button onClick={() => onEdit(w)} data-testid={`workout-edit-${w.id}`} className="p-1.5 text-zinc-500 hover:text-white"><Pencil size={14} /></button>
            <button onClick={() => onDelete(w)} data-testid={`workout-delete-${w.id}`} className="p-1.5 text-zinc-500 hover:text-[#E63946]"><Trash2 size={14} /></button>
          </span>
        ) : (
          <span className="flex items-center gap-1">{Array.from({ length: w.level }).map((_, i) => <Flame key={i} size={12} className="text-[#E63946]" />)}</span>
        )}
      </div>
      <h3 className="mt-3 font-heading text-2xl font-bold uppercase text-white">{w.title[lang]}</h3>
      <p className="mt-1 text-sm text-zinc-400">{w.focus[lang]}</p>
      <div className="mt-4 flex items-center gap-4 text-xs font-mono text-zinc-500">
        <span>{w.minutes} min</span>
        <span>{w.blocks.filter((b) => b.kind === "round").length} round</span>
        <span className="text-[#E63946] font-bold group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
          <Play size={12} /> {lang === "it" ? "Avvia" : "Start"}
        </span>
      </div>
    </div>
  );
}

function ProgramList({ lang, customs, onStart, onEdit, onDelete, onCreate }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Dumbbell size={18} className="text-[#E63946]" />
        <h2 className="font-heading text-2xl font-extrabold uppercase text-white">{lang === "it" ? "Programmi di Allenamento" : "Training Programs"}</h2>
      </div>
      <p className="text-sm text-zinc-400 mb-6">
        {lang === "it" ? "5 allenamenti guidati di livello intermedio, a difficoltà crescente. Segui il player passo-passo." : "5 guided intermediate workouts, increasing difficulty. Follow the step-by-step player."}
      </p>
      <div className="grid sm:grid-cols-2 gap-4">
        {WORKOUTS.map((w) => <ProgramCard key={w.id} w={w} lang={lang} onStart={onStart} />)}
      </div>

      <div className="mt-10 flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Star size={18} className="text-[#E63946]" />
          <h2 className="font-heading text-2xl font-extrabold uppercase text-white">{lang === "it" ? "I miei programmi" : "My programs"}</h2>
        </div>
        <button onClick={onCreate} data-testid="create-program-btn" className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#E63946] hover:bg-[#D90429] text-white text-sm font-bold uppercase rounded-md">
          <Plus size={15} /> {lang === "it" ? "Crea" : "Build"}
        </button>
      </div>
      {customs.length ? (
        <div className="grid sm:grid-cols-2 gap-4">
          {customs.map((w) => <ProgramCard key={w.id} w={w} lang={lang} onStart={onStart} onEdit={onEdit} onDelete={onDelete} />)}
        </div>
      ) : (
        <button onClick={onCreate} data-testid="create-program-empty" className="w-full rounded-xl border border-dashed border-[#3a3f4b] p-8 text-sm text-zinc-400 hover:text-white hover:border-[#E63946]">
          {lang === "it" ? "Nessun programma ancora. Costruisci il tuo allenamento su misura: round, combo, recuperi." : "No programs yet. Build your own workout: rounds, combos, rest."}
        </button>
      )}
    </div>
  );
}

function WorkoutPlayer({ workout, onExit }) {
  const { lang } = useLang();
  const blocks = workout.blocks;
  const [idx, setIdx] = useState(0);
  const [remaining, setRemaining] = useState(blocks[0].seconds);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const ref = useRef(null);
  const announced = useRef(-1);
  const P = coachPhrases(lang);
  const lines = useMemo(() => [...blocks.map((b) => blockLine(b, lang)), P.ten, ...P.count, P.done], [blocks, lang]); // eslint-disable-line
  const coach = useCoachVoice(lines);

  const block = blocks[idx];
  const meta = KIND_META[block.kind];

  const goTo = (newIdx, autostart) => {
    if (newIdx < 0) return;
    if (newIdx >= blocks.length) {
      setRunning(false);
      setDone(true);
      bell();
      coach.say(P.done);
      return;
    }
    setIdx(newIdx);
    setRemaining(blocks[newIdx].seconds);
    if (autostart) setRunning(true);
  };

  useEffect(() => {
    if (running && announced.current !== idx) {
      announced.current = idx;
      setTimeout(() => coach.say(blockLine(blocks[idx], lang)), 700);
    }
  }, [running, idx]); // eslint-disable-line

  useEffect(() => () => coach.stop(), []); // eslint-disable-line

  useEffect(() => {
    if (!running) return;
    ref.current = setInterval(() => {
      setRemaining((r) => {
        if (r > 1) {
          const kind = blocks[idx].kind;
          if (r === 11 && blocks[idx].seconds >= 30 && kind !== "rest" && kind !== "cooldown") coach.say(P.ten);
          if (r <= 4) coach.enabled ? coach.say(P.count[r - 2]) : beep(720, 0.12, "sine");
          return r - 1;
        }
        bell();
        goTo(idx + 1, true);
        return 0;
      });
    }, 1000);
    return () => clearInterval(ref.current);
  }, [running, idx, coach.enabled]); // eslint-disable-line

  const start = () => { if (!running) bell(); else coach.stop(); setRunning(!running); };

  const saveSession = async () => {
    try {
      await api.post("/blueprint/sessions", {
        rounds_completed: blocks.filter((b) => b.kind === "round").length,
        total_minutes: workout.minutes,
        rpe: Math.min(10, 4 + (workout.level || 2)),
        strikes: 0,
        notes: `${workout.title[lang]} (${LEVEL_LABEL[lang][workout.level]})`,
        combos: [],
      }, authHeader());
      toast.success(lang === "it" ? "Sessione salvata!" : "Session saved!");
      onExit();
    } catch (e) {
      toast.error("Error");
    }
  };

  if (done) {
    return (
      <div className="max-w-xl mx-auto text-center py-10">
        <CheckCircle2 size={56} className="text-[#10B981] mx-auto" />
        <h2 className="mt-4 font-heading text-3xl font-black uppercase text-white">{lang === "it" ? "Allenamento completato!" : "Workout complete!"}</h2>
        <p className="mt-2 text-zinc-400">{workout.title[lang]} · {workout.minutes} min</p>
        <div className="mt-6 flex gap-3 justify-center">
          <button onClick={saveSession} data-testid="workout-save-btn" className="px-6 py-3 bg-[#E63946] hover:bg-[#D90429] text-white font-bold uppercase rounded-md">{lang === "it" ? "Salva nel log" : "Save to log"}</button>
          <button onClick={onExit} data-testid="workout-exit-btn" className="px-6 py-3 border border-[#272b35] text-zinc-300 rounded-md">{lang === "it" ? "Esci" : "Exit"}</button>
        </div>
      </div>
    );
  }

  const pct = (idx / blocks.length) * 100;

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center justify-between gap-3 mb-4">
        <button onClick={onExit} data-testid="workout-back-btn" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white min-w-0">
          <ArrowLeft size={15} className="shrink-0" /> <span className="truncate">{workout.title[lang]}</span>
        </button>
        <button onClick={() => { coach.stop(); coach.setEnabled(!coach.enabled); }} data-testid="coach-voice-toggle"
          className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase border transition-colors ${coach.enabled ? "border-[#E63946] text-white bg-[#E63946]/15" : "border-[#272b35] text-zinc-500"}`}>
          {coach.enabled ? <Volume2 size={13} /> : <VolumeX size={13} />}
          {lang === "it" ? "Voce coach" : "Coach voice"}
          {coach.enabled && !coach.ready && <span data-testid="coach-voice-loading" className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] animate-pulse" />}
        </button>
      </div>

      <div className="h-1.5 bg-[#14161b] rounded-full overflow-hidden mb-6">
        <div className="h-full bg-[#E63946] transition-all" style={{ width: `${pct}%` }} />
      </div>

      <div className="rounded-2xl border p-6 sm:p-8" style={{ borderColor: meta.color, backgroundColor: `${meta.color}0d` }}>
        <span className="text-xs font-mono uppercase tracking-[0.25em]" style={{ color: meta.color }}>{meta.label[lang]} · {idx + 1}/{blocks.length}</span>
        <h3 className="mt-2 font-heading text-2xl sm:text-3xl font-extrabold uppercase text-white">{block.name[lang]}</h3>
        <div data-testid="workout-timer" className="font-mono-data font-bold text-white text-6xl sm:text-7xl my-4 tabular-nums">{fmt(remaining)}</div>
        <ul className="space-y-2 mb-2">
          {block.cues.map((c, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-zinc-200">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: meta.color }} />
              {c[lang]}
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-6 flex items-center justify-center gap-3">
        <button onClick={() => goTo(idx - 1, false)} data-testid="workout-prev-btn" disabled={idx === 0} className="p-3 border border-[#272b35] text-white rounded-md disabled:opacity-40"><SkipBack size={18} /></button>
        {!running ? (
          <button onClick={start} data-testid="workout-play-btn" className="inline-flex items-center gap-2 px-8 py-3 bg-[#E63946] hover:bg-[#D90429] text-white font-bold uppercase rounded-md"><Play size={18} /> {lang === "it" ? "Avvia" : "Start"}</button>
        ) : (
          <button onClick={start} data-testid="workout-pause-btn" className="inline-flex items-center gap-2 px-8 py-3 bg-[#F59E0B] text-black font-bold uppercase rounded-md"><Pause size={18} /> {lang === "it" ? "Pausa" : "Pause"}</button>
        )}
        <button onClick={() => { bell(); goTo(idx + 1, running); }} data-testid="workout-next-btn" className="p-3 border border-[#272b35] text-white rounded-md"><SkipForward size={18} /></button>
      </div>

      {idx + 1 < blocks.length && (
        <p className="mt-5 text-center text-xs font-mono text-zinc-500">{lang === "it" ? "Prossimo" : "Up next"}: <span className="text-zinc-300">{blocks[idx + 1].name[lang]}</span></p>
      )}
    </div>
  );
}
